import * as routes from 'pages/routes';
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { Card, IconButton, Pane, Popover, Position, Spinner, Text, Tooltip } from 'evergreen-ui';
import truncate from 'lodash/truncate';
import Button from 'UI/Button';
import cn from 'classnames';
import { BackgroundTaskStatus } from 'resources/background-tasks/background-task.schema';
import { CloseIcon } from 'public/icons';
import BackgroundTaskService from 'resources/background-tasks/background-task.service';
import { useMutation, useQueryClient } from 'react-query';
import dynamic from 'next/dynamic';
import styles from './styles.module.scss';
import animationData from './new-transaction-celebration.json';

const Lottie = dynamic(() => import('react-lottie'), { ssr: false });

const TaskStatusLabel = {
  PENDING: 'Processing',
  COMPLETED: 'Complete',
};

const defaultOptions = {
  loop: false,
  autoplay: true,
  animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const DismissTaskButton = ({ taskId }) => {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(async () => {
    await BackgroundTaskService.updateOne({
      item_id: taskId,
    }, {
      status: BackgroundTaskStatus.DISMISSED,
    });

    await queryClient.invalidateQueries(['background-tasks']);
  });

  return (
    <Popover
      position={Position.TOP_LEFT}
      content={(
        <Pane
          width={200}
          padding={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Text fontSize={12} lineHeight="16px" fontWeight={300} marginBottom={10} color="var(--gray-500)" fontFamily="'Merriweather Sans', sans-serif">
            Are you sure you want to dismiss this task?
          </Text>
          <Button
            isLoading={isLoading}
            intent="danger"
            appearance="outlined"
            height={30}
            width={70}
            onClick={mutate}
          >
            Dismiss
          </Button>
        </Pane>
  )}
    >
      <IconButton
        appearance="minimal"
        size="small"
        icon={CloseIcon}
        isLoading={isLoading}
      />
    </Popover>
  );
};

DismissTaskButton.propTypes = {
  taskId: PropTypes.string.isRequired,
};

const TaskStatus = ({ task }) => {
  const statusAction = useMemo(() => {
    switch (task.status) {
      case 'PENDING':
        return (
          <Spinner size={16} marginRight={4} />
        );
      case 'COMPLETED':
        const link = `${routes.path.newTransactionScanReview}?aiTransactionId=${task.item_id}`;
        return (
          <Link href={link} passHref>
            <Button width="auto !important" height={24}>
              Review
            </Button>
          </Link>
        );
      default:
        return null;
    }
  }, [task]);

  return (
    <Pane display="flex" alignItems="center" justifyContent="space-between">
      <Text fontSize={14} fontWeight={300} fontStyle="italic" lineHeight="16px" textAlign="left" color="var(--gray-500)" fontFamily="'Merriweather Sans', sans-serif">
        {TaskStatusLabel[task.status]}
      </Text>
      {statusAction}
    </Pane>
  );
};

TaskStatus.propTypes = {
  task: PropTypes.shape({
    item_id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

const AITaskCard = ({ task }) => {
  const { metadata } = task;

  if (!task) return null;

  return (
    <Card className={cn(styles.AITaskCard, styles[task.status])}>
      {
        task.status === BackgroundTaskStatus.COMPLETED && (
          <Pane className={styles.animationContainer}>
            <Lottie
              options={defaultOptions}
              height="inherit"
              width="inherit"
              isClickToPauseDisabled
            />
          </Pane>
        )
      }
      <Pane className={styles.detailsContainer}>
        <Pane className={styles.fileNameContainer}>
          <Tooltip content={metadata?.originalFileName ?? task.item_id} position={Position.TOP}>
            <Text fontWeight="bold" className={styles.titleLabel}>
              {truncate(metadata?.originalFileName ?? task.item_id, { length: 16 })}
            </Text>
          </Tooltip>
          <DismissTaskButton taskId={task.item_id} />
        </Pane>
        <TaskStatus task={task} />
      </Pane>
    </Card>
  );
};

AITaskCard.propTypes = {
  task: PropTypes.shape({
    item_id: PropTypes.string.isRequired,
    metadata: PropTypes.shape({
      originalFileName: PropTypes.string.isRequired,
    }).isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

export default AITaskCard;
